import {
  GroupType,
  GroupingRule,
  FilterInfoOperator,
  FormattingRuleOperator,
  FieldFormattingVersion,
  APIEntityType,
} from '@ardoq/api-types';
import { PerspectiveGroupingRule } from './Groupings/types';
import {
  AsyncBackgroundOperation,
  AsyncBackgroundOperationType,
  OperationType,
} from './types';
import {
  CompleteComponentOrReferenceFormattingRule,
  CompleteTagFormattingRule,
  ComponentOrReferenceFormattingRule,
  FormattingRule,
} from '@ardoq/data-model';
import { isNil, isNumber, isString } from 'lodash';
import { isColorRangeFormatting } from '@ardoq/api-types';
import { isSameSet } from '@ardoq/common-helpers';

export const calculateConfirmationModalState = (
  asyncBackgroundOperation: AsyncBackgroundOperation,
  expectedOperationType: OperationType
) => {
  if (
    asyncBackgroundOperation.type === 'NONE' ||
    asyncBackgroundOperation.operation !== expectedOperationType
  ) {
    return {
      isConfirmationModalShown: false,
      isInProgress: false,
      isOperationFailed: false,
      errorMessage: null,
    };
  }

  const typesTypesWithDisplayedModal: AsyncBackgroundOperationType[] = [
    'WAITING_FOR_CONFIRMATION',
    'OPERATION_FAILED',
    'IN_PROGRESS',
  ];
  const isConfirmationModalShown = typesTypesWithDisplayedModal.includes(
    asyncBackgroundOperation.type
  );
  const isInProgress = asyncBackgroundOperation.type === 'IN_PROGRESS';
  const isOperationFailed =
    asyncBackgroundOperation.type === 'OPERATION_FAILED';
  const errorMessage =
    (isOperationFailed && asyncBackgroundOperation.errorMessage) ?? null;

  return {
    isConfirmationModalShown,
    isInProgress,
    isOperationFailed,
    errorMessage,
  };
};

export const perspectiveGroupingRuleToGroupingRule = (
  perspectiveGroupingRule: PerspectiveGroupingRule
): GroupingRule => {
  switch (perspectiveGroupingRule.type) {
    case 'WORKSPACE':
      return {
        type: GroupType.WORKSPACE,
        sublabel: perspectiveGroupingRule.sublabel,
      };
    case 'COMPONENT':
      return {
        type: GroupType.COMPONENT,
      };
    case 'REFERENCE':
      return {
        type: GroupType.REFERENCE,
        targetId: perspectiveGroupingRule.targetId,
        workspaceId: perspectiveGroupingRule.workspaceId,
        direction: perspectiveGroupingRule.direction,
        sublabel: perspectiveGroupingRule.sublabel,
      };
    case 'PARENT':
      return {
        type: GroupType.PARENT,
        workspaceId: perspectiveGroupingRule.workspaceId,
        targetId: perspectiveGroupingRule.targetId,
        sublabel: perspectiveGroupingRule.sublabel,
      };
    case 'PARENT_ALL':
      return {
        type: GroupType.PARENT_ALL,
        sublabel: perspectiveGroupingRule.sublabel,
      };
    case 'TAG':
      return {
        type: GroupType.TAG,
        targetId: perspectiveGroupingRule.targetId,
        workspaceId: perspectiveGroupingRule.workspaceId,
      };
    case 'FIELD':
      return {
        type: GroupType.FIELD,
        targetId: perspectiveGroupingRule.targetId,
        workspaceId: perspectiveGroupingRule.workspaceId,
      };
    case 'CHILD':
      return {
        type: GroupType.CHILD,
        targetId: perspectiveGroupingRule.targetId,
        workspaceId: perspectiveGroupingRule.workspaceId,
      };
    default:
      return {
        type: GroupType.ALL,
      };
  }
};

export const isBooleanComparator = (
  comparator: FilterInfoOperator | FormattingRuleOperator
): boolean => {
  return (
    comparator === FilterInfoOperator.BOOLEAN ||
    comparator === FormattingRuleOperator.NOT_BOOLEAN
  );
};

type CompleteFormattingRule =
  | CompleteTagFormattingRule
  | CompleteComponentOrReferenceFormattingRule;

/**
 * Check if a formatting rule is using legacy formatting (no version or old version).
 * @deprecated Remove when BE has migrated all rules to v1
 */
export const isLegacyFormattingRule = (rule?: FormattingRule): boolean => {
  if (!rule?.version) {
    return true;
  }

  return (
    isNumber(rule.version) &&
    rule.version < FieldFormattingVersion.NEW_CONDITIONAL_FORMATTING
  );
};

type FormattingRuleCompletenessInput = {
  hasComparator: boolean;
  comparator?: ComponentOrReferenceFormattingRule['comparator'];
  value: FormattingRule['value'];
};

/** Check if a comparator/value pair forms a condition that can actually match something */
export const hasCompleteFormattingCondition = ({
  hasComparator,
  comparator,
  value,
}: FormattingRuleCompletenessInput): boolean => {
  // Consider checkbox comparators complete even without a value
  if (
    hasComparator &&
    comparator !== undefined &&
    isBooleanComparator(comparator)
  ) {
    return true;
  }

  // IS_EMPTY operator allows null as a valid value
  if (hasComparator && comparator === FormattingRuleOperator.IS_EMPTY) {
    return value === null;
  }

  // Check for color range formatting completeness
  if (value !== undefined && value !== null && isColorRangeFormatting(value)) {
    const isHasMinAndMax =
      !isNil(value?.min?.value) && !isNil(value?.max?.value);
    return isHasMinAndMax;
  }

  // An empty list-field colour map or an empty multi-select colours nothing
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).length > 0;
  }

  return !isNil(value) && !(isString(value) && value.trim() === '');
};

/** Check if a formatting rule has a complete value */
export const isCompleteFormattingRule = (
  rule: FormattingRule
): rule is CompleteFormattingRule => {
  // Older formatting rules without version are considered complete
  if (isLegacyFormattingRule(rule)) {
    return true;
  }

  const hasComparator = 'comparator' in rule;

  return hasCompleteFormattingCondition({
    hasComparator,
    comparator: hasComparator ? rule.comparator : undefined,
    value: rule.value,
  });
};

export const isRuleVersionSupported = (
  isNewConditionalFormatting: boolean,
  version: FieldFormattingVersion | undefined
): boolean => {
  if (!isNewConditionalFormatting) return true;
  if (version === undefined) return false;
  return version !== FieldFormattingVersion.ORIGINAL;
};

export const isComponentOrReferenceFormattingRule = (
  rule: FormattingRule
): rule is ComponentOrReferenceFormattingRule =>
  rule.type === APIEntityType.COMPONENT ||
  rule.type === APIEntityType.REFERENCE;

export const canSquashListField = (
  prev: ComponentOrReferenceFormattingRule,
  next: ComponentOrReferenceFormattingRule
): boolean => {
  return (
    prev.type === next.type &&
    prev.field === next.field &&
    isSameSet(prev.typeNames ?? [], next.typeNames ?? []) &&
    prev.comparator === next.comparator
  );
};
